import {
  faChevronDown,
  faChevronUp,
  faCircleMinus,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { Collapse } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import classNames from 'classnames';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CardV2 as Card, Icon } from '@feathr/components';

import * as styles from './ExpandableSection.css';

interface IExpandableSectionProps {
  /**
   * Rendered in the header's title slot. Shrinks before the actions do, so a
   * self-sizing control like `InlineEdit` can't push them out of the card.
   */
  title: ReactNode;
  /** The body, revealed when the section is expanded. */
  children: ReactNode;
  /** Base for this section's `data-name` and those of its action buttons. */
  name: string;
  defaultOpen?: boolean;
  /**
   * Forces the section open and removes the collapse toggle, so a half-finished
   * edit can't be hidden away.
   */
  editing?: boolean;
  /** Hides the delete action. */
  disabled?: boolean;
  /** Drives the header's complete/incomplete indicator. */
  isComplete?: boolean;
  /** Omit to render no delete action. */
  onDelete?: () => void;
}

/**
 * One item of an editable collection, as a card that expands in place to reveal its body.
 * The header carries a caller-supplied title, a complete/incomplete indicator, an
 * optional delete action, and a collapse toggle.
 *
 * Deliberately not `StepSection`: that numbers each item and draws a connector to the
 * next, which reads as an ordered sequence. The collections here are unordered.
 *
 * @param props - {@link IExpandableSectionProps}.
 */
function ExpandableSection({
  title,
  children,
  name,
  defaultOpen = false,
  editing = false,
  disabled = false,
  isComplete = false,
  onDelete,
}: Readonly<IExpandableSectionProps>): JSX.Element {
  const { t } = useTranslation();
  const [isToggledOpen, { toggle: toggleOpen }] = useDisclosure(defaultOpen);
  const isOpen = editing || isToggledOpen;

  return (
    <Card name={name} width={'full'}>
      <div
        className={classNames(styles.header, { [styles.isOpen]: isOpen })}
        data-name={`${name}-header`}
      >
        <div className={styles.title}>{title}</div>
        <div className={styles.actions}>
          {!!onDelete && !disabled && (
            <Button
              aria-label={t('Delete')}
              name={`${name}-header-delete`}
              onClick={onDelete}
              type={'icon-small'}
            >
              <Icon icon={faTrash} />
            </Button>
          )}
          {/* `title` names the svg, so the indicator isn't a colour-only signal. */}
          <Icon
            className={classNames({ [styles.isComplete]: isComplete })}
            icon={isComplete ? faCircleCheck : faCircleMinus}
            title={isComplete ? t('Complete') : t('Incomplete')}
          />
          {!editing && (
            <Button
              aria-label={isOpen ? t('Collapse') : t('Expand')}
              name={`${name}-header-toggle`}
              onClick={toggleOpen}
              type={'icon-small'}
            >
              <Icon icon={isOpen ? faChevronUp : faChevronDown} />
            </Button>
          )}
        </div>
      </div>
      {/* Collapse keeps the closed body mounted but aria-hidden, so only role queries
          reflect whether it's reachable. */}
      <Collapse in={isOpen}>
        <div className={styles.content} data-name={`${name}-content`}>
          {children}
        </div>
      </Collapse>
    </Card>
  );
}

export default ExpandableSection;
